import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { initials } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, ShieldCheck, Ban, MessageCircle, Trash2, RotateCcw, Users } from "lucide-react";

const BAN_PRESETS = ["Scam/fraud", "Spam", "Abusive behaviour", "Prohibited items", "Fake listings", "Impersonation", "Payment dispute"];
const FILTERS = [["all", "All"], ["verified", "Verified"], ["banned", "Banned"], ["active", "Most active"]];

export function AdminUsers({ focusUser }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(focusUser || null);
  const [banReason, setBanReason] = useState("");
  const [msg, setMsg] = useState("");

  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users", query, filter],
    queryFn: () => {
      const p = new URLSearchParams();
      if (query) p.set("search", query);
      p.set("filter", filter);
      return apiFetch(`/admin/users?${p.toString()}`);
    },
  });

  const { data: detail } = useQuery({ queryKey: ["admin-user", openId], queryFn: () => apiFetch(`/admin/users/${openId}`), enabled: !!openId });

  useEffect(() => { if (focusUser) setOpenId(focusUser); }, [focusUser]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-user", openId] }); };

  const ban = async (banned) => {
    try { await apiFetch(`/admin/users/${openId}/ban`, { method: "POST", body: JSON.stringify({ banned, reason: banReason.trim() }) }); toast.success(banned ? "User suspended" : "User reinstated"); setBanReason(""); refresh(); }
    catch (err) { toast.error(err.message); }
  };
  const sendMsg = async () => {
    if (!msg.trim()) return;
    try { await apiFetch(`/admin/users/${openId}/message`, { method: "POST", body: JSON.stringify({ message: msg.trim() }) }); toast.success("Message sent"); setMsg(""); }
    catch (err) { toast.error(err.message); }
  };
  const removeUser = async () => {
    if (!window.confirm("Delete this user account? This hides all their listings.")) return;
    try { await apiFetch(`/admin/users/${openId}`, { method: "DELETE" }); toast.success("User deleted"); setOpenId(null); refresh(); }
    catch (err) { toast.error(err.message); }
  };
  const restoreListings = async () => {
    try { const r = await apiFetch(`/admin/users/${openId}/restore-listings`, { method: "POST" }); toast.success(`Restored ${r.restored} listing(s)`); refresh(); }
    catch (err) { toast.error(err.message); }
  };

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }} className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input data-testid="admin-user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone…" className="flex-1 bg-transparent text-sm outline-none" />
      </form>
      <div className="mt-3 flex gap-2 overflow-x-auto">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${filter === k ? "bg-brand text-white" : "bg-secondary"}`}>{label}</button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {users.length === 0 ? (
          <EmptyState icon={Users} title="No users found" />
        ) : (
          users.map((u) => (
            <button key={u.id} onClick={() => setOpenId(u.id)} data-testid={`admin-user-${u.id}`} className="flex w-full items-center gap-3 rounded-2xl border border-border p-3 text-left transition hover:bg-secondary">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">{initials(u.name)}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1"><span className="truncate font-semibold">{u.name}</span>{u.verified && <ShieldCheck className="h-3.5 w-3.5 text-sky-600" />}{u.banned && <span className="rounded-full bg-red-100 px-1.5 text-[10px] font-bold text-red-600">BANNED</span>}</div>
                <p className="truncate text-xs text-muted-foreground">{u.email} · {u.listing_count} listings</p>
              </div>
            </button>
          ))
        )}
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-1">{detail.name}{detail.verified && <ShieldCheck className="h-4 w-4 text-sky-600" />}</DialogTitle></DialogHeader>
              <div className="text-sm text-muted-foreground">{detail.email} · {detail.phone}</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-secondary p-2"><p className="font-bold">{detail.active_count}</p><p className="text-[11px] text-muted-foreground">Active</p></div>
                <div className="rounded-xl bg-secondary p-2"><p className="font-bold">{detail.sold_count}</p><p className="text-[11px] text-muted-foreground">Sold</p></div>
                <div className="rounded-xl bg-secondary p-2"><p className="font-bold">{detail.total_views}</p><p className="text-[11px] text-muted-foreground">Views</p></div>
                <div className="rounded-xl bg-secondary p-2"><p className="font-bold">{detail.offers_made}</p><p className="text-[11px] text-muted-foreground">Offers made</p></div>
                <div className="rounded-xl bg-secondary p-2"><p className="font-bold">{detail.offers_received}</p><p className="text-[11px] text-muted-foreground">Offers recv</p></div>
                <div className="rounded-xl bg-secondary p-2"><p className="font-bold">{detail.removed_count}</p><p className="text-[11px] text-muted-foreground">Removed</p></div>
              </div>

              {!detail.is_admin && (
                <div className="space-y-3 rounded-xl border border-border p-3">
                  <p className="text-sm font-semibold">Moderation</p>
                  {!detail.banned ? (
                    <>
                      <div className="flex flex-wrap gap-1.5">
                        {BAN_PRESETS.map((r) => (
                          <button key={r} onClick={() => setBanReason(r)} className={`rounded-full px-2.5 py-1 text-xs ${banReason === r ? "bg-brand text-white" : "bg-secondary"}`}>{r}</button>
                        ))}
                      </div>
                      <input value={banReason} onChange={(e) => setBanReason(e.target.value)} placeholder="Ban reason" className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none" data-testid="ban-reason-input" />
                      <button onClick={() => ban(true)} className="flex items-center gap-1.5 rounded-lg bg-destructive px-3 py-2 text-sm font-semibold text-white" data-testid="ban-user-btn"><Ban className="h-4 w-4" />Suspend user</button>
                    </>
                  ) : (
                    <button onClick={() => ban(false)} className="flex items-center gap-1.5 rounded-lg bg-price px-3 py-2 text-sm font-semibold text-white" data-testid="unban-user-btn"><RotateCcw className="h-4 w-4" />Reinstate user</button>
                  )}
                </div>
              )}

              <div className="space-y-2 rounded-xl border border-border p-3">
                <p className="text-sm font-semibold">Message user</p>
                <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Message from Venda Team…" className="w-full rounded-lg border border-input p-2 text-sm outline-none" data-testid="admin-message-input" />
                <button onClick={sendMsg} className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white" data-testid="admin-send-message"><MessageCircle className="h-4 w-4" />Send</button>
              </div>

              {detail.removed_count > 0 && (
                <button onClick={restoreListings} className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-semibold"><RotateCcw className="h-4 w-4" />Restore all removed listings</button>
              )}
              {!detail.is_admin && (
                <button onClick={removeUser} className="flex items-center gap-1.5 text-sm font-medium text-destructive" data-testid="delete-user-btn"><Trash2 className="h-4 w-4" />Delete account</button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
